// Snap: the classic memory flip. A fun game: no gems, just a good time and one charm.

import { h, clear } from '@kit/dom';
import { shuffle } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';

const ICONS = ['circle', 'square', 'triangle', 'star', 'heart', 'moon', 'diamond', 'hexagon', 'leaf', 'drop', 'bolt', 'cloud'];
const COLORS = ['#e2574c', '#f08a3c', '#f5c542', '#5fbf6a', '#3bb3a9', '#4f8fe6', '#9b6be6', '#ee86b7', '#8a5a3b', '#2a2a2e', '#3bb3a9', '#e2574c'];

function iconSvg(name: string, color: string): string {
  const p: Record<string, string> = {
    circle: `<circle cx="20" cy="20" r="13" fill="${color}"/>`,
    square: `<rect x="8" y="8" width="24" height="24" rx="4" fill="${color}"/>`,
    triangle: `<path d="M20 6 L34 32 L6 32Z" fill="${color}"/>`,
    star: `<path d="M20 5 L24 16 L36 16 L26 23 L30 35 L20 28 L10 35 L14 23 L4 16 L16 16Z" fill="${color}"/>`,
    heart: `<path d="M20 34 L8 22 A7 7 0 0 1 20 12 A7 7 0 0 1 32 22Z" fill="${color}"/>`,
    moon: `<path d="M26 6 A14 14 0 1 0 34 26 A10 10 0 0 1 26 6Z" fill="${color}"/>`,
    diamond: `<path d="M20 5 L34 20 L20 35 L6 20Z" fill="${color}"/>`,
    hexagon: `<path d="M20 5 L33 12 L33 28 L20 35 L7 28 L7 12Z" fill="${color}"/>`,
    leaf: `<path d="M8 32 C8 14 20 6 34 6 C34 22 24 32 8 32Z" fill="${color}"/>`,
    drop: `<path d="M20 5 C26 15 32 20 32 26 A12 12 0 0 1 8 26 C8 20 14 15 20 5Z" fill="${color}"/>`,
    bolt: `<path d="M23 4 L9 22 L19 22 L16 36 L31 16 L21 16Z" fill="${color}"/>`,
    cloud: `<path d="M12 30 A6 6 0 0 1 12 18 A8 8 0 0 1 27 16 A6 6 0 0 1 30 30Z" fill="${color}"/>`,
  };
  return `<svg viewBox="0 0 40 40" width="100%" height="100%">${p[name]}</svg>`;
}

function mount(host: HTMLElement, ctx: GameContext) {
  let pairs = 0;
  const pickRow = h('div.stack', null,
    h('h3.center', null, 'How many pairs?'),
    h('div.grid3', null,
      h('button.btn', { onclick: () => start(6) }, '6'),
      h('button.btn', { onclick: () => start(8) }, '8'),
      h('button.btn', { onclick: () => start(12) }, '12'),
    ),
  );
  host.append(pickRow);

  function start(n: number) {
    pairs = n;
    clear(host);
    const ids = shuffle(ctx.rng, ICONS.map((_, i) => i)).slice(0, n);
    const deck = shuffle(ctx.rng, [...ids, ...ids]);
    const cols = n <= 6 ? 3 : 4;
    let open: number[] = [];
    let matched = new Set<number>();
    let moves = 0;
    let busy = false;
    const t0 = performance.now();
    const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Moves: ', h('strong', null, '0')), h('span.muted', null, 'Pairs: ', h('strong', null, `0/${n}`)));
    const grid = h('div', { style: { display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '8px' } });
    host.append(head, grid);
    const cards: HTMLElement[] = deck.map((id, i) => {
      const el = h('button', {
        onclick: () => flip(i),
        style: { aspectRatio: '1', borderRadius: '12px', border: '2px solid var(--line)', background: 'var(--fun)', padding: '14%' },
      });
      void id;
      return el;
    });
    cards.forEach(c => grid.append(c));

    function render() {
      deck.forEach((id, i) => {
        const el = cards[i];
        const show = open.includes(i) || matched.has(i);
        el.style.background = show ? 'var(--surface)' : 'var(--fun)';
        el.innerHTML = show ? iconSvg(ICONS[id], COLORS[id]) : '';
        el.style.opacity = matched.has(i) ? '.55' : '1';
      });
      (head.firstElementChild!.querySelector('strong') as HTMLElement).textContent = String(moves);
      (head.lastElementChild!.querySelector('strong') as HTMLElement).textContent = `${matched.size / 2}/${n}`;
    }

    function flip(i: number) {
      if (busy || open.includes(i) || matched.has(i)) return;
      open.push(i);
      ctx.sound('tick');
      render();
      if (open.length === 2) {
        moves++;
        const [a, b] = open;
        if (deck[a] === deck[b]) {
          matched.add(a); matched.add(b); open = [];
          ctx.sound('ding');
          render();
          if (matched.size === deck.length) {
            if (n === 12) ctx.charm('charm.snap12');
            ctx.say(`All ${n} pairs in ${moves} moves!`);
            setTimeout(() => ctx.end({ attempts: moves, correct: n, perfect: moves === n, durationMs: performance.now() - t0, notes: [`${moves} moves for ${n} pairs`] }), 700);
          }
        } else {
          busy = true;
          setTimeout(() => { open = []; busy = false; render(); }, 750);
        }
      }
    }
    render();
  }

  return { destroy() { clear(host); } };
}

const mod: GameModule = {
  id: 'fun.snap',
  title: 'Snap',
  blurb: 'Flip two cards. Find the pairs. The classic memory game.',
  category: 'fun',
  skills: [],
  levelRange: [1, 10],
  unlock: { gems: 150 },
  mount,
};
export default mod;
